import { create } from 'zustand';
import { QBTestQuestionEto } from '../lib/api/types';
import { useWhiteboardStore } from './useWhiteboardStore';
import { useUserStore } from './useUserStore';
import { useTenantStore } from './useTenantStore';
import { OpticalAnswer } from '../types';

interface TestState {
  // Test data
  currentTestId: string | null;
  currentQuestionId: string | null;
  questions: QBTestQuestionEto[];
  groups: any[];
  testTitle: string;
  totalTime?: number;

  // Test session
  isTestMode: boolean;
  isTestCompleted: boolean;
  sessionStartTime: number | null;
  sessionCount: number;

  // Optical answers (includes '0' for visited but unanswered questions)
  opticalAnswers: Record<string, OpticalAnswer>;
  eliminatedChoices: Record<string, string>;
  
  // Navigation
  currentQuestionIndex: number;
  selectedGroupIndex: number | null;
  
  // Actions - Test management
  setCurrentTestId: (testId: string | null) => void;
  setCurrentQuestionId: (questionId: string | null) => void;
  setQuestions: (questions: QBTestQuestionEto[]) => void;
  setGroups: (groups: any[]) => void;
  setTestTitle: (title: string) => void;
  setTotalTime: (time: number | undefined) => void;
  
  // Actions - Session management
  startTestSession: () => void;
  endTestSession: () => void;
  setTestCompleted: (completed: boolean) => void;
  incrementSessionCount: () => void;
  
  // Actions - Answers
  setOpticalAnswer: (questionId: string, answer: OpticalAnswer | null) => void;
  setOpticalAnswers: (answers: Record<string, OpticalAnswer>) => void;
  toggleEliminatedChoice: (questionId: string, choice: string) => void;
  clearAnswers: () => void;
  
  // Actions - Navigation
  setCurrentQuestionIndex: (index: number) => void;
  setSelectedGroupIndex: (index: number | null) => void;
  goToNextQuestion: () => void;
  goToPreviousQuestion: () => void;
}

export const useTestStore = create<TestState>((set, get) => ({
  // Initial state
  currentTestId: null,
  currentQuestionId: null,
  questions: [],
  groups: [],
  testTitle: '',
  totalTime: undefined,
  isTestMode: false,
  isTestCompleted: false,
  sessionStartTime: null,
  sessionCount: 0,
  opticalAnswers: {},
  eliminatedChoices: {},
  currentQuestionIndex: 0,
  selectedGroupIndex: null,
  
  // Test management
  setCurrentTestId: (testId) => set({ currentTestId: testId }),
  setCurrentQuestionId: (questionId) => set({ currentQuestionId: questionId }),
  setQuestions: (questions) => set({ questions }),
  setGroups: (groups) => set({ groups }),
  setTestTitle: (title) => set({ testTitle: title }),
  setTotalTime: (time) => set({ totalTime: time, isTestMode: !!time }),
  
  // Session management
  startTestSession: () => set({
    sessionStartTime: Date.now(),
    isTestCompleted: false
  }),
  
  endTestSession: () => set({
    sessionStartTime: null,
    isTestCompleted: true
  }),
  
  setTestCompleted: (completed) => set({ isTestCompleted: completed }),
  incrementSessionCount: () => set((state) => ({ sessionCount: state.sessionCount + 1 })),
  
  // Answer management
  setOpticalAnswer: (questionId, answer) => set((state) => {
       
    // Get the most up-to-date whiteboard store state
    const whiteboardStore = useWhiteboardStore.getState();
    
    // Get current cache and ensure we have the latest data
    const currentCache = whiteboardStore.questionDataCache;
    let questionData = currentCache.get(questionId);
    
    // If no question data exists, create it
    if (!questionData) {
      const userStore = useUserStore.getState();
      const tenantStore = useTenantStore.getState();
      
      questionData = {
        questionId,
        userId: userStore?.userInfo?.id || 0,
        tenantId: tenantStore?.tenantId || 0,
        testId: state.currentTestId || '',
        selectionHistory: '', // Always start with empty history
        trajectory: [],
        totalTime: 0,
        updatedAt: Date.now()
      };
      
    }
    
    // Get current selection history and current answer
    // Make sure we get the latest data from the cache
    const latestQuestionData = whiteboardStore.questionDataCache.get(questionId) || questionData;
    
    // Check if we have a previous update in progress - if so, use that selection history
    const previousOpticalAnswer = state.opticalAnswers[questionId];
    let currentSelectionHistory = latestQuestionData.selectionHistory || '';
    
    // If we have a previous answer but no selection history, reconstruct it
    if (previousOpticalAnswer && !currentSelectionHistory) {
      currentSelectionHistory = previousOpticalAnswer;
    }
    
    const currentAnswer = state.opticalAnswers[questionId];
    
   
    
    // Calculate new selection history with improved logic
    let newSelectionHistory = currentSelectionHistory;
    let trajectoryChanged = false;
    
    if (answer === null) {
      // Clearing answer - add '0' only if we currently have an answer and history doesn't already end with '0'
      if (currentAnswer && !currentSelectionHistory.endsWith('0')) {
        newSelectionHistory = currentSelectionHistory + '0';
        trajectoryChanged = true;
       
      } else {
       
      }
    } else {
      // Selecting an answer
      const lastChar = currentSelectionHistory.slice(-1);
      
      // Always add to history when selecting a different answer
      // This tracks the complete selection path, like studentAnswer does
      if (answer !== currentAnswer) {
        newSelectionHistory = currentSelectionHistory + answer;
        trajectoryChanged = true;
      
      } else {
        // Re-clicking the same answer that's already selected - no change
      }
    }
    
    // Log trajectory analysis
   
    
    // Always update cache with current state
    const updatedQuestionData = {
      ...latestQuestionData,
      selectionHistory: newSelectionHistory,
      opticalChoice: answer === null ? '0' as '0' : answer,
      updatedAt: Date.now()
    };
    
    // Update cache atomically to prevent race conditions
    const mostCurrentCache = useWhiteboardStore.getState().questionDataCache;
    const newCache = new Map(mostCurrentCache);
    newCache.set(questionId, updatedQuestionData);
    useWhiteboardStore.getState().setQuestionDataCache(newCache);
    
   
    
    // Sync whiteboard store optical answers
    if (answer === null) {
      const newAnswers = { ...whiteboardStore.opticalAnswers };
      delete newAnswers[questionId];
      whiteboardStore.setOpticalAnswers(newAnswers);
    } else {
      whiteboardStore.setOpticalAnswers({
        ...whiteboardStore.opticalAnswers,
        [questionId]: answer
      });
    }
    
    // Update test store optical answers
    if (answer === null) {
      const { [questionId]: _, ...rest } = state.opticalAnswers;
      return { opticalAnswers: rest };
    }
    return {
      opticalAnswers: { ...state.opticalAnswers, [questionId]: answer }
    };
  }),
  
  setOpticalAnswers: (answers) => set({ opticalAnswers: answers }),
  
  toggleEliminatedChoice: (questionId, choice) => set((state) => {
    const current = state.eliminatedChoices[questionId] || '';
    const choices = current.split('');
    const index = choices.indexOf(choice);
    
    if (index >= 0) {
      choices.splice(index, 1);
    } else {
      choices.push(choice);
    }
    
    const newChoices = choices.sort().join('');
    
    if (newChoices) {
      return {
        eliminatedChoices: { ...state.eliminatedChoices, [questionId]: newChoices }
      };
    } else {
      const { [questionId]: _, ...rest } = state.eliminatedChoices;
      return { eliminatedChoices: rest };
    }
  }),
  
  clearAnswers: () => set({
    opticalAnswers: {},
    eliminatedChoices: {}
  }),
  
  // Navigation
  setCurrentQuestionIndex: (index) => set((state) => {
    const newIndex = Math.max(0, Math.min(index, state.questions.length - 1));
    const question = state.questions[newIndex];
    return {
      currentQuestionIndex: newIndex,
      currentQuestionId: question?.questionId || null
    };
  }),
  
  setSelectedGroupIndex: (index) => set({ selectedGroupIndex: index }),
  
  goToNextQuestion: () => set((state) => {
    const newIndex = Math.min(state.currentQuestionIndex + 1, state.questions.length - 1);
    const question = state.questions[newIndex];
    return {
      currentQuestionIndex: newIndex,
      currentQuestionId: question?.questionId || null
    };
  }),
  
  goToPreviousQuestion: () => set((state) => {
    const newIndex = Math.max(state.currentQuestionIndex - 1, 0);
    const question = state.questions[newIndex];
    return {
      currentQuestionIndex: newIndex,
      currentQuestionId: question?.questionId || null
    };
  })
}));